import { useContext } from "react";

import { observer } from "mobx-react-lite";

import { ExpressionNode } from "@/components/expression/ExpressionNode";
import { RowContent } from "@/components/expression/inline-node/wrapping/RowContent";
import { hintLightTheme } from "@/components/expression/theme/corca-themes";
import { FlatNode } from "@/components/expression/utils/FlatNode";
import * as Node from "@/components/expression/utils/layout";
import { SelectedContext } from "@/components/expression/utils/layout";
import { NodeStyleProvider, useNodeStyle } from "@/components/expression/utils/NodeStyleProvider";
import { CAPTURE_CURSOR_CLASS_NAME } from "@/models/CursorModel";
import { twMerge } from "@/utils/twMerge";

import { MathThemeContext } from "../theme";

import type { InlineNodeContentRenderItem } from "@/components/expression/inline-node/types";
import type { RowNode } from "@/components/expression/inline-node/wrapping/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { InlineNodeModel } from "@/models/nodes/InlineNodeModel";

export type RowProps = {
  node: InlineNodeModel | undefined;
  doc: DocumentModel;
  /**
   * The content tree for this row, produced by `createWrappedRows`.
   */
  content: RowNode[];
  isFirstRow: boolean;
  isLastRow: boolean;
  withFillers: boolean;
  onItemSize?: (key: string, width: number) => void;
  splitBy?: InlineNodeContentRenderItem | null;
  indentation?: number;
  rowId?: string;
  className?: string;
};

export const Row = observer(function Row({
  node,
  doc,
  content,
  isFirstRow,
  isLastRow,
  withFillers,
  onItemSize,
  splitBy,
  indentation = 0,
  rowId,
  className,
}: RowProps) {
  const isContextSelected = useContext(SelectedContext);
  const { renderContext, view, debugOutlineNodes } = useNodeStyle();
  const isTextView = view === "text";
  const isSelected = isContextSelected || (node && doc.selection.isSelected(node));
  const shouldRenderLeadingFiller = isFirstRow && !!node && withFillers && node.nodes.length > 0;

  return (
    <Node.Dimensions
      dimension="tight"
      className={twMerge(
        CAPTURE_CURSOR_CLASS_NAME,
        "flex flex-nowrap flex-1 leading-0 p-0 items-baseline justify-start whitespace-nowrap rounded-lg",
        "transition-spacing motion-reduce:transition-none",
        isTextView && "min-w-0 min-h-1 items-center",
        debugOutlineNodes && "border p-1",
        renderContext === "editor" && isSelected && "bg-selected-bg text-selected-fg",
        className,
      )}
      style={indentation > 0 ? { paddingLeft: indentation } : undefined}
      data-inline-node-row-id={rowId}
      data-node-id={node?.id}
    >
      {shouldRenderLeadingFiller && (
        <NodeStyleProvider placeholderVisible="none">
          <FlatNode inlineNode={node} index={0} className="flex-1" cursorPosition="end" />
        </NodeStyleProvider>
      )}

      <RowContent
        content={content}
        onItemSize={onItemSize}
        suppressedCursor={
          shouldRenderLeadingFiller ? { parentInline: node, inlineIndex: 0 } : undefined
        }
        externallyHandledTrailingFor={node}
      />

      {splitBy?.node && (
        <NodeStyleProvider renderContext="objects-tree">
          <MathThemeContext.Provider value={hintLightTheme}>
            <ExpressionNode node={splitBy.node} className="opacity-30" />
          </MathThemeContext.Provider>
        </NodeStyleProvider>
      )}

      {isLastRow && node && (
        <FlatNode
          inlineNode={node}
          index={node.nodes.length}
          className={withFillers ? "flex-1" : undefined}
        />
      )}
    </Node.Dimensions>
  );
});
